/*
 * Used as part of GOGET react-native application
 *
 *  - intended for display map in mobile app via WebView
 *  - not intended for use elsewhere
 */

import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MapContainer,
  ImageOverlay,
  Marker,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import { ConsumerAction, Resource } from "@gogetcorp/floor-map-bridge";
// components
import { RoomIcon } from "./components/RoomIcon";
import { DeskIcon } from "./components/DeskIcon";
import { ReservationsIcon } from "./components/ReservationsIcon";
import { WayfinderIcon } from "./components/WayfinderIcon";
import { RNPolygonal } from "./components/RNPolygonal";
import { RNCircle } from "./components/RNCircle";
import { TooltipName } from "../../../components/shared/TooltipName/TooltipName";
import { ContrastName } from "../../../components/shared/ContrastName/ContrastName";
// containers
import { MessageListener } from "./containers/MessageListener";
import { MapBoundsController } from "./containers/MapBoundsController";
import { PanToController } from "./containers/PanToController";
// utils
import { sendMessageToRn } from "./utils/send-message-to-rn";
import { getImageSize } from "./utils/overlay.utils";
import { getMapTooltipSizeRatio } from "./utils/getMapTooltipSizeRatio";
import {
  getPoiIcon,
  PoiIconType,
} from "../../../components/Floors/lib/poiMarkersHelper";

import "./RNMapView.scss";

export const trimText = (text: string, limit: number = 30) => {
  if (!text) {
    return "";
  }

  if (text.length < limit) {
    return text;
  }

  return text.substring(0, limit) + "...";
};

type Size = {
  width: number;
  height: number;
};

const initialZoom = 0;
const maxZoom = 6;
const minZoom = -6;
const style = {
  minHeight: "100vh",
  maxHeight: "100vh",
  backgroundColor: "transparent",
};
const center: L.LatLngTuple = [0, 0];
export const GREEN = "#01EC96";
export const RED = "#EA1D25";
export const OPACITY = 0.3;

const useWindowSize = () => {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const onResize = useCallback(() => {
    if (
      window.innerHeight !== size.height ||
      window.innerWidth !== size.width
    ) {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    }
  }, [size]);

  useEffect(() => {
    document.addEventListener("resize", onResize);

    return () => {
      document.removeEventListener("resize", onResize);
    };
  }, [onResize]);

  return [size, onResize] as const;
};

const RNMapView: FC = () => {
  // state
  const [windowSize, refreshWindowSize] = useWindowSize();
  const [overlay, setOverlay] = useState<
    ConsumerAction.OverlaySet["payload"] | null
  >(null);

  const [markers, setMarkers] = useState<ConsumerAction.MarkersSet["payload"]>({
    rooms: [],
    desks: [],
    wayfinders: [],
    pois: [],
  });

  const [bgColor, setBgColor] =
    useState<ConsumerAction.SetBgColor["payload"]>("#FFFFFF");
  const [mapConfig, setMapConfig] = useState<Required<Resource.MapConfig>>({
    deskMarkerSize: 50,
    roomMarkerSize: 80,
    wayfinderMarkerWidth: 88,
    poisMarkerSize: 48,
    scaleFactor: 0.7,
  });

  const [focusedMarkerId, setFocusedMarkerId] = useState<string | null>(null);
  const [isBoundsReady, setIsBoundsReady] = useState<boolean>(false);
  const [activeReservationMarkerIds, setActiveReservationMarkerIds] = useState<
    string[]
  >([]);
  // memo
  const [mapImageSize, setMapImageSize] = useState<Size | null>(null);
  const overlayBounds = useMemo<L.LatLngBoundsLiteral | null>(() => {
    if (!mapImageSize) {
      return null;
    }

    return [
      [mapImageSize.height / 2, mapImageSize.width / 2],
      [-mapImageSize.height / 2, -mapImageSize.width / 2],
    ];
  }, [mapImageSize]);
  const mapBounds = useMemo<L.LatLngBoundsLiteral | null>(() => {
    if (!mapImageSize) {
      return null;
    }
    function getSideShouldFit(
      container: Size,
      image: Size
    ): "width" | "height" {
      const widthRatio = container.width / image.width;
      const heightRatio = container.height / image.height;

      return widthRatio > heightRatio ? "height" : "width";
    }

    const sideShouldFit = getSideShouldFit(windowSize, mapImageSize);

    if (sideShouldFit === "width") {
      return [
        [0, mapImageSize.width / 2],
        [-0, -mapImageSize.width / 2],
      ];
    }

    return [
      [mapImageSize.height / 2, 0],
      [-mapImageSize.height / 2, -0],
    ];
  }, [mapImageSize, windowSize]);
  const mapMaxBounds = useMemo<L.LatLngBoundsLiteral | null>(() => {
    if (!overlayBounds) {
      return null;
    }

    return [
      [overlayBounds[0][0] * 1.6, overlayBounds[0][1]],
      [overlayBounds[1][0] * 1.6, overlayBounds[1][1]],
    ];
  }, [overlayBounds]);
  // refs
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    sendMessageToRn({
      type: "state",
      payload: "idle",
    });
  }, []);

  useEffect(() => {
    if (overlay && mapImageSize && isBoundsReady) {
      sendMessageToRn({
        type: "state",
        payload: "ready",
      });
    }
  }, [overlay, mapImageSize, isBoundsReady]);

  useEffect(() => {
    if (overlay) {
      setMapImageSize(null);
      getImageSize(overlay.url).then(setMapImageSize);
    }
  }, [overlay]);

  const handleRoomMarkerPress = useCallback(
    (roomId: string, customerId: string) => () => {
      sendMessageToRn({
        type: "room-click",
        payload: {
          customerId,
          roomId,
        },
      });
    },
    []
  );

  const handleDeskMarkerPress = useCallback(
    (deskId: string, customerId: string) => () => {
      sendMessageToRn({
        type: "desk-click",
        payload: {
          customerId,
          deskId,
        },
      });
    },
    []
  );

  const handleReservationMarkerPress = useCallback(
    (idMarker: string) => () => {
      setActiveReservationMarkerIds((prev) => {
        if (prev.includes(idMarker)) {
          setFocusedMarkerId((_focusedMarker) => {
            if (_focusedMarker === idMarker) {
              return null;
            }

            return _focusedMarker;
          });
          return [...prev].filter((id) => id !== idMarker);
        }

        setFocusedMarkerId(idMarker);
        return [idMarker];
      });
    },
    []
  );

  const handleMapConfigChange = useCallback((config: Resource.MapConfig) => {
    setMapConfig((prev) => ({
      ...prev,
      ...config,
    }));
  }, []);

  const handleMapCenter = useCallback(() => {
    setTimeout(() => {
      refreshWindowSize();
    }, 1000);
    setFocusedMarkerId(null);
    mapRef.current?.panTo(center);
    if (mapBounds) {
      mapRef.current?.fitBounds(mapBounds);
    }
  }, [mapBounds, refreshWindowSize]);

  const mapTooltipSizeRatio = getMapTooltipSizeRatio(mapConfig.scaleFactor);

  return (
    <div style={{ backgroundColor: bgColor }}>
      <MapContainer
        ref={mapRef}
        crs={L.CRS.Simple}
        minZoom={minZoom}
        maxZoom={maxZoom}
        zoom={initialZoom}
        center={center}
        zoomControl={false}
        zoomSnap={0.15}
        zoomDelta={0.25}
        style={style}
        attributionControl={false}
      >
        <MapBoundsController
          mapBounds={mapBounds}
          mapMaxBounds={mapMaxBounds}
          onReady={setIsBoundsReady}
        />
        <PanToController markers={markers} markerIdToFocus={focusedMarkerId} />
        {!!overlay && !!overlayBounds && (
          <ImageOverlay
            url={overlay.url}
            opacity={overlay.opacity}
            bounds={overlayBounds}
          />
        )}

        {markers?.pois?.map((poiMarker) => {
          if (!poiMarker?.marker) return null;

          return (
            <Marker
              key={poiMarker.id}
              icon={getPoiIcon(poiMarker.type as PoiIconType, {
                size: [mapConfig.poisMarkerSize, mapConfig.poisMarkerSize],
              })}
              position={[poiMarker.marker.latitude, poiMarker.marker.longitude]}
              draggable={false}
            />
          );
        })}

        {markers?.rooms?.map(
          ({
            lat,
            lng,
            id,
            customerId,
            isBooked,
            isBlocked,
            circle,
            polygonal,
            name,
          }) => {
            const roomHasMarker = lat !== 0 || lng !== 0;

            let shouldRenderMarker = false;
            if (overlay?.showRoomMarkers) {
              shouldRenderMarker = roomHasMarker;
            } else {
              shouldRenderMarker =
                roomHasMarker &&
                (circle === null ||
                  circle?.latitude === 0 ||
                  circle?.longitude === 0) &&
                polygonal?.length <= 0;
            }

            return (
              <Fragment key={id}>
                {shouldRenderMarker && (
                  <Marker
                    key={id}
                    position={[lat, lng]}
                    eventHandlers={{
                      click: handleRoomMarkerPress(id, customerId),
                    }}
                    icon={RoomIcon({
                      isBooked,
                      isFocused: id === focusedMarkerId,
                      size: mapConfig.roomMarkerSize,
                    })}
                  >
                    {overlay?.showTooltips && (
                      <Tooltip
                        key={`tooltip-${id}-${isBooked}`}
                        className={`room-permanent-tooltip`}
                        direction="bottom"
                        offset={[0, -4]}
                        opacity={1}
                        permanent
                      >
                        {overlay?.hasContrastTooltips ? (
                          <ContrastName
                            name={trimText(name, 18)}
                            scaleFactor={mapConfig.scaleFactor}
                            sizeRatio={mapTooltipSizeRatio}
                            isClientApp
                            isMarker
                          />
                        ) : (
                          <TooltipName
                            name={name}
                            isBooked={isBooked}
                            scaleFactor={mapConfig.scaleFactor}
                            sizeRatio={mapTooltipSizeRatio}
                          />
                        )}
                      </Tooltip>
                    )}
                  </Marker>
                )}

                {circle &&
                  (circle?.latitude !== 0 || circle?.longitude !== 0) && (
                    <RNCircle
                      circle={circle}
                      name={name}
                      isBooked={isBooked}
                      showRoomMarkers={overlay?.showRoomMarkers}
                      showTooltips={overlay?.showTooltips}
                      scaleFactor={mapConfig.scaleFactor}
                      mapReady={isBoundsReady}
                      hasContrastTooltips={overlay?.hasContrastTooltips}
                    />
                  )}

                {polygonal?.length > 0 && (
                  <RNPolygonal
                    id={id}
                    polygonal={polygonal}
                    name={name}
                    isBooked={isBooked}
                    showRoomMarkers={overlay?.showRoomMarkers}
                    showTooltips={overlay?.showTooltips}
                    scaleFactor={mapConfig.scaleFactor}
                    mapReady={isBoundsReady}
                    hasContrastTooltips={overlay?.hasContrastTooltips}
                    eventHandlers={{
                      click: handleRoomMarkerPress(id, customerId),
                    }}
                  />
                )}
              </Fragment>
            );
          }
        )}

        {markers.desks?.map(
          ({ lat, lng, id, customerId, isBooked, reservations, isBlocked }) => (
            <Fragment key={id}>
              {reservations.length > 0 && (
                <Marker
                  key={`${id}-reservation`}
                  position={[lat, lng]}
                  eventHandlers={{
                    click: handleReservationMarkerPress(`${id}-reservation`),
                  }}
                  icon={ReservationsIcon({
                    isActive: activeReservationMarkerIds.includes(
                      `${id}-reservation`
                    ),
                    reservations,
                  })}
                  zIndexOffset={
                    activeReservationMarkerIds.includes(`${id}-reservation`)
                      ? 2
                      : 1
                  }
                />
              )}
              <Marker
                key={id}
                position={[lat, lng]}
                eventHandlers={{
                  click: isBlocked
                    ? () => {}
                    : handleDeskMarkerPress(id, customerId),
                }}
                icon={DeskIcon({
                  isBooked,
                  id,
                  isBlocked,
                  isFocused: id === focusedMarkerId,
                  size: mapConfig.deskMarkerSize,
                })}
              />
            </Fragment>
          )
        )}
        {markers.wayfinders?.map(({ lat, lng, id, name }) => (
          <Marker
            key={id}
            position={[lat, lng]}
            icon={WayfinderIcon({
              width: mapConfig.wayfinderMarkerWidth,
            })}
          >
            <Tooltip
              className={`room-permanent-tooltip`}
              direction="bottom"
              offset={[0, 0]}
              opacity={1}
              permanent
            >
              <div>
                <span>{trimText(name, 18)}</span>
              </div>
            </Tooltip>
          </Marker>
        ))}
        <MessageListener
          zoom={{ initial: initialZoom, min: minZoom, max: maxZoom }}
          onOverlayUrlChange={setOverlay}
          onMarkerFocusChange={setFocusedMarkerId}
          onMarkersChange={setMarkers}
          onBgColorChange={setBgColor}
          onMapConfigChange={handleMapConfigChange}
          onMarkerReservationFocusSet={(markerReservationId) => {
            setActiveReservationMarkerIds((prev) => {
              return [markerReservationId, ...prev];
            });
          }}
          onMarkerReservationFocusClear={(markerReservationId) => {
            setActiveReservationMarkerIds((prev) => {
              return prev.filter((id) => id !== markerReservationId);
            });
          }}
          onMapCenter={handleMapCenter}
        />
      </MapContainer>
    </div>
  );
};

export { RNMapView };
